// AddEditNotification.js - Main Wizard Component
import React, { createContext, useState } from 'react';
import { Box, Button, Card, Typography, Stepper, Step, StepLabel,CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, People, FilterAlt, Schedule, Description, Send } from '@mui/icons-material';
import { useForm, FormProvider } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Swal from 'sweetalert2';
import { NotificationServices } from './NotificationServices';
import { LocalStore } from '../../../storage/authStore';

// Step components
import FilterSection from './Steps/FilterSection';
import ScheduleAndMode from './Steps/ScheduleAndMode';
import ReviewAndSend from './Steps/ReviewAndSend';
import AudienceSelection from './Steps/AudienceSelection';
import NotificationContent from './Steps/NotificationContent';
import { PermissionGuard } from '../../../components/PermissionGuard';

const NotificationDataContext = createContext();

const AddEditNotification = ({setTabValue}) => {
    const navigate = useNavigate();
    
    const initialState = {
        // Step 1: Audience Selection
        audienceType: '',
        
        // Step 2: Filters
        customerFilters: {
            selectedCustomers: [],
            locationFilters: {
                        locationFilter: 'radius',
                        centerCoordinates: { lat: '', lng: '' },
                        radius: '',
                        polygon: []
                    },
            filterOptions:[]
        },
        expertFilters: {
            selectedExperts: [],
            locationFilters: {
                        locationFilter: 'radius',
                        centerCoordinates: { lat: '', lng: '' },
                        radius: '',
                        polygon: []
                    },
            filterOptions:[]
        },
        
        // Step 3: Schedule & Mode
        scheduleType: 'now',
        scheduledDateTime: null,
        deliveryModes: ['push'],
        
        // Step 4: Content
        title: '',
        body: ''
    };

    const [notificationData, setNotificationData] = useState(initialState);
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        { label: 'Select Audience', icon: <People /> },
        { label: 'Apply Filters', icon: <FilterAlt /> },
        { label: 'Schedule & Mode', icon: <Schedule /> },
        { label: 'Content', icon: <Description /> },
        { label: 'Review & Send', icon: <Send /> }
    ];

    const NotificationFormMethod = useForm({
        mode: 'onChange',
        defaultValues: notificationData
    });

    const { reset } = NotificationFormMethod;

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setCompletedSteps((prev) => {
                prev.add(activeStep);
                return new Set(prev);
            });
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
        }
    };
    const [completedSteps, setCompletedSteps] = useState(new Set());

    const isStepComplete = (step) => {
        switch (step) {
            case 0:
                return notificationData.audienceType !== '';
            case 1:
                if (notificationData.audienceType === 'customer') {
                    return notificationData.customerFilters.selectedCustomers.length > 0;
                } else if (notificationData.audienceType === 'expert') {
                    return notificationData.expertFilters.selectedExperts.length > 0;
                }
                return false;
            case 2:
                return notificationData.deliveryModes.length > 0 && completedSteps.has(1) && notificationData.scheduleType == 'later'? notificationData.scheduledDateTime !== null : true;
            case 3:
                return notificationData.title.trim() !== '' && notificationData.body.trim() !== '';
            case 4:
                return completedSteps.has(3); // Review step is always complete (just a summary)
            default:
                return false;
        }
    };

    const handleFormSubmit = async () => { 
        const selectedAudience = notificationData.audienceType === 'customer' ? notificationData.customerFilters.selectedCustomers : notificationData.expertFilters.selectedExperts; 
        const convertIdObjectsToStrings = selectedAudience.map(item => String(item.id));
        const formatData = { 
            customerIds: notificationData.audienceType === 'customer' ? convertIdObjectsToStrings : [], 
            expertIds: notificationData.audienceType === 'expert' ? convertIdObjectsToStrings : [], 
            title: notificationData.title, 
            body: notificationData.body, 
            type: 'campaign', 
            event: 'custom', 
            audienceType: notificationData.audienceType, 
            scheduleType: notificationData.scheduleType, 
            scheduledDateTime: notificationData.scheduledDateTime, 
            deliveryModes: notificationData.deliveryModes, 
            senderUserId: LocalStore.getCurrentUserDetails().id, 
            filters: notificationData.audienceType === 'customer' ? notificationData.customerFilters : notificationData.expertFilters
        }; 
        console.log("formatted Data:", formatData); 
        const result = await Swal.fire({ 
            title: 'Are you sure?', 
            text: `You want to send notification to ${selectedAudience.length} ${notificationData.audienceType}(s)?`, 
            icon: 'warning', 
            showCancelButton: true, 
            confirmButtonText: 'Yes', 
            cancelButtonText: 'No', 
            allowOutsideClick: false,  
        }); 
        if (result.isConfirmed) { 
            Swal.fire({ 
                title: 'Processing...', 
                text: `Please wait while we ${notificationData.scheduleType === 'now' ? 'send' : 'schedule'} notification`, 
                allowOutsideClick: false, 
                didOpen: () => { 
                    Swal.showLoading(); 
                }, 
            }); 
            
            
            try { 
            const createResponse = await NotificationServices.sendNotification(formatData); 
            if (createResponse.success == "1") { 
            await Swal.fire({ 
                title: 'Success', 
                text: `Notification ${notificationData.scheduleType === 'now' ? 'sent' : 'scheduled'} successfully`, 
                icon: 'success', 
                confirmButtonText: 'OK', 
                timer: 1600, 
            }); 
            reset(initialState); 
            setNotificationData(initialState); 
            setActiveStep(0); 
            // navigate('/notifications'); 
            //Navigate back to notifications list 
            setTabValue('1');
            } else { 
                await Swal.fire({ 
                title: 'Failed', 
                text: createResponse.message || 'Notification operation failed', 
                icon: 'error', 
                confirmButtonText: 'OK', 
                }); 
            } 
            } catch (error) { 
                console.error('Error submitting notification:', error); 
                await Swal.fire({ 
                    title: 'Error', 
                    text: 'An unexpected error occurred', 
                    icon: 'error', 
                    confirmButtonText: 'OK', 
                }); 
            } 
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0: return <AudienceSelection />;
            case 1: return <FilterSection />;
            case 2: return <ScheduleAndMode />;
            case 3: return <NotificationContent />;
            case 4: return <ReviewAndSend onSubmit={handleFormSubmit} />;
            default: return null;
        }
    };
    const ColorStepIcon = (props) => {
    const { active, completed, icon } = props;

    return (
        <Box
        sx={{
            borderRadius: "50%",
            p:'20%',
            display: "flex",
            alignItems: "center",
            bgcolor: completed
            ? "success.main"   // green if completed
            : active
            ? "primary.main"   // primary if active
            : "text.secondary", // grey otherwise
            color: "#fff"   // grey otherwise
        }}
        >
        {React.cloneElement(icon, {
            fontSize: "small"
        })}
        </Box>
    );
    };
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <NotificationDataContext.Provider value={{ notificationData,  setNotificationData, activeStep, setActiveStep , initialState }}>
                <FormProvider {...NotificationFormMethod}>
                    <Box>
                        {/* Stepper */}
                        <Card sx={{ mb: 1 }}>
                            <CardContent>
                                <Stepper activeStep={activeStep} alternativeLabel>
                                    {steps.map((step, index) => (
                                        <Step key={step.label} completed={completedSteps.has(index)}>
                                            <StepLabel 
                                                StepIconComponent={(props) => (
                                                    <ColorStepIcon {...props} icon={step.icon} />
                                                )}
                                                sx={{
                                                    '& .MuiStepLabel-label': {
                                                        mt:'2%',
                                                        fontSize: '0.8rem',
                                                        fontWeight: activeStep === index || isStepComplete(index) ? 600 : 400,
                                                        color: activeStep === index
                                                                ? "primary.main" // active step
                                                                : completedSteps.has(index)
                                                                ? "success.main" // completed step
                                                                : "text.secondary",
                                                    }
                                                }}
                                            >
                                                {step.label}
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </CardContent>
                        </Card>

                        {/* Step Content */}
                        <Card>
                            <CardContent sx={{ height:'max-content' }}>
                                {renderStepContent(activeStep)}
                            </CardContent>
                        </Card>

                        {/* Navigation Buttons */}
                        <PermissionGuard feature='notification' permission={'canCreate'}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Button
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                    sx={{ fontSize: '0.75rem' }}
                                >
                                    Back
                                </Button>
                                
                                {activeStep < steps.length - 1 ? (
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!isStepComplete(activeStep)}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Next
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={handleFormSubmit}
                                        disabled={!isStepComplete(activeStep)}
                                        sx={{ fontSize: '0.75rem' }}
                                    >
                                        Send Notification
                                    </Button>
                                )}
                            </Box>
                        </PermissionGuard>
                    </Box>
                </FormProvider>
            </NotificationDataContext.Provider>
        </LocalizationProvider>
    );
};

export default AddEditNotification;
export { NotificationDataContext };
